import { GripVertical } from "lucide-react";
import * as ResizablePrimitive from "react-resizable-panels";
import { cn } from "../../lib/utils";

/**
 * Thin token-aligned wrapper over react-resizable-panels (v4). The library
 * manages each group's flex direction internally; the handle keys off the
 * separator's `aria-orientation` to draw a vertical bar in horizontal groups
 * and a horizontal bar in vertical (stacked) groups.
 */
function ResizablePanelGroup({
  className,
  ...props
}: React.ComponentProps<typeof ResizablePrimitive.Group>) {
  return <ResizablePrimitive.Group className={cn("h-full w-full", className)} {...props} />;
}

const ResizablePanel = ResizablePrimitive.Panel;

function ResizableHandle({
  withHandle,
  className,
  ...props
}: React.ComponentProps<typeof ResizablePrimitive.Separator> & {
  withHandle?: boolean;
}) {
  return (
    <ResizablePrimitive.Separator
      className={cn(
        "relative flex items-center justify-center bg-border focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1",
        "aria-[orientation=vertical]:h-full aria-[orientation=vertical]:w-px",
        "aria-[orientation=horizontal]:h-px aria-[orientation=horizontal]:w-full",
        "[&[aria-orientation=horizontal]>div]:rotate-90",
        className,
      )}
      {...props}
    >
      {withHandle ? (
        <div className="z-10 flex h-4 w-3 items-center justify-center rounded-sm border border-border bg-border">
          <GripVertical className="size-2.5" />
        </div>
      ) : null}
    </ResizablePrimitive.Separator>
  );
}

// Re-exported so consumers reach the library's panel hook without a direct
// react-resizable-panels dependency (the package owns that surface, ADR-0024).
export { usePanelRef } from "react-resizable-panels";

export { ResizablePanelGroup, ResizablePanel, ResizableHandle };
